/**
 * Capacity Planning Utilities for hermit-μDC
 *
 * Provides estimates and recommendations for:
 * - Resource usage forecasting
 * - Cost optimization suggestions
 * - Upgrade path recommendations
 */

import { DatacenterConfig, DatacenterNode, ScenarioType } from '../types/configurator';
import { SCENARIO_PRESETS } from '../data/scenarios';

// ============================================
// INTERFACES
// ============================================

export interface ResourceForecast {
  currentUsage: ResourceUsage;
  projectedUsage: ResourceUsage[];
  bottlenecks: Bottleneck[];
  recommendations: string[];
}

export interface ResourceUsage {
  month: number;
  cpu: { used: number; total: number; percent: number };
  memory: { used: number; total: number; percent: number };
  storage: { used: number; total: number; percent: number };
  network: { used: number; total: number; percent: number };
}

export interface Bottleneck {
  resource: 'cpu' | 'memory' | 'storage' | 'network';
  monthsUntilFull: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  recommendation: string;
}

export interface CostOptimization {
  currentMonthlyCost: number;
  optimizedMonthlyCost: number;
  annualSavings: number;
  suggestions: CostSuggestion[];
}

export interface CostSuggestion {
  category: 'hardware' | 'power' | 'cooling' | 'licensing' | 'maintenance';
  title: string;
  description: string;
  estimatedSavings: number;
  effort: 'low' | 'medium' | 'high';
  impact: 'low' | 'medium' | 'high';
}

export interface UpgradePath {
  currentTier: string;
  upgradeTier: string;
  reason: string;
  estimatedCost: number;
  benefits: string[];
  timeline: string;
  priority: 'optional' | 'recommended' | 'critical';
}

// ============================================
// RESOURCE FORECASTING
// ============================================

export function forecastResourceUsage(
  config: DatacenterConfig,
  growthRatePercent: number = 10,
  forecastMonths: number = 12
): ResourceForecast {
  const totalResources = calculateTotalResources(config.nodes);
  const currentUsage = estimateCurrentUsage(totalResources, config.scenario);

  // Project usage over time
  const projectedUsage: ResourceUsage[] = [];
  for (let month = 1; month <= forecastMonths; month++) {
    const growthFactor = Math.pow(1 + growthRatePercent / 100, month / 12);
    projectedUsage.push({
      month,
      cpu: {
        used: Math.round(currentUsage.cpu.used * growthFactor),
        total: currentUsage.cpu.total,
        percent: Math.min(100, (currentUsage.cpu.used * growthFactor / currentUsage.cpu.total) * 100),
      },
      memory: {
        used: Math.round(currentUsage.memory.used * growthFactor),
        total: currentUsage.memory.total,
        percent: Math.min(100, (currentUsage.memory.used * growthFactor / currentUsage.memory.total) * 100),
      },
      storage: {
        used: Math.round(currentUsage.storage.used * growthFactor),
        total: currentUsage.storage.total,
        percent: Math.min(100, (currentUsage.storage.used * growthFactor / currentUsage.storage.total) * 100),
      },
      network: {
        used: Math.round(currentUsage.network.used * growthFactor),
        total: currentUsage.network.total,
        percent: Math.min(100, (currentUsage.network.used * growthFactor / currentUsage.network.total) * 100),
      },
    });
  }

  // Identify bottlenecks
  const bottlenecks = identifyBottlenecks(currentUsage, projectedUsage);
  const recommendations = generateForecastRecommendations(bottlenecks, config);

  return {
    currentUsage,
    projectedUsage,
    bottlenecks,
    recommendations,
  };
}

function calculateTotalResources(nodes: DatacenterNode[]): {
  cpu: number;
  memory: number;
  storage: number;
  network: number;
} {
  return nodes.reduce(
    (acc, node) => ({
      cpu: acc.cpu + node.specs.cpuCores,
      memory: acc.memory + node.specs.memoryGB,
      storage: acc.storage + node.specs.storageGB,
      network: acc.network + 10, // Assume 10Gbps per node
    }),
    { cpu: 0, memory: 0, storage: 0, network: 0 }
  );
}

function estimateCurrentUsage(
  total: { cpu: number; memory: number; storage: number; network: number },
  scenario: ScenarioType
): ResourceUsage {
  // Usage factors based on scenario
  const usageFactors: Record<ScenarioType, number> = {
    minimal: 0.3,
    'lab-bench': 0.4,
    research: 0.6,
    industrial: 0.5,
    'gdpr-llm': 0.7,
    'ai-rendering': 0.8,
    'lab-testing': 0.5,
    custom: 0.5,
  };

  const factor = usageFactors[scenario] || 0.5;

  return {
    month: 0,
    cpu: {
      used: Math.round(total.cpu * factor),
      total: total.cpu,
      percent: factor * 100,
    },
    memory: {
      used: Math.round(total.memory * factor),
      total: total.memory,
      percent: factor * 100,
    },
    storage: {
      used: Math.round(total.storage * factor * 0.4), // Storage fills slower
      total: total.storage,
      percent: factor * 40,
    },
    network: {
      used: Math.round(total.network * factor * 0.3), // Network rarely saturated
      total: total.network,
      percent: factor * 30,
    },
  };
}

function identifyBottlenecks(
  _current: ResourceUsage,
  projected: ResourceUsage[]
): Bottleneck[] {
  const bottlenecks: Bottleneck[] = [];
  const resources: Array<'cpu' | 'memory' | 'storage' | 'network'> = [
    'cpu',
    'memory',
    'storage',
    'network',
  ];

  for (const resource of resources) {
    // Find when resource hits 80% and 100%
    const warningMonth = projected.findIndex((p) => p[resource].percent >= 80);
    const criticalMonth = projected.findIndex((p) => p[resource].percent >= 95);

    if (warningMonth !== -1 || criticalMonth !== -1) {
      const monthsUntil = criticalMonth !== -1 ? criticalMonth : warningMonth;
      const severity =
        monthsUntil <= 3 ? 'critical' : monthsUntil <= 6 ? 'high' : monthsUntil <= 9 ? 'medium' : 'low';

      const recommendations: Record<string, string> = {
        cpu: 'Add compute nodes or upgrade to higher core count processors',
        memory: 'Add RAM to existing nodes or add new nodes with more memory',
        storage: 'Add storage nodes or expand existing storage capacity',
        network: 'Upgrade network interfaces or add network capacity',
      };

      bottlenecks.push({
        resource,
        monthsUntilFull: monthsUntil + 1,
        severity,
        recommendation: recommendations[resource],
      });
    }
  }

  return bottlenecks.sort((a, b) => a.monthsUntilFull - b.monthsUntilFull);
}

function generateForecastRecommendations(
  bottlenecks: Bottleneck[],
  config: DatacenterConfig
): string[] {
  const recommendations: string[] = [];

  if (bottlenecks.length === 0) {
    recommendations.push(
      'Current capacity is adequate for projected growth over the forecast period'
    );
    return recommendations;
  }

  // Critical bottlenecks
  const critical = bottlenecks.filter((b) => b.severity === 'critical');
  if (critical.length > 0) {
    recommendations.push(
      `URGENT: ${critical.map((b) => b.resource).join(', ')} capacity will be exhausted within 3 months`
    );
  }

  // Specific recommendations based on scenario
  if (config.scenario === 'ai-rendering' || config.scenario === 'gdpr-llm') {
    if (bottlenecks.some((b) => b.resource === 'memory')) {
      recommendations.push(
        'Consider upgrading to nodes with 128GB+ RAM for AI workloads'
      );
    }
  }

  // Storage recommendations
  if (bottlenecks.some((b) => b.resource === 'storage')) {
    recommendations.push('Implement data lifecycle policies to manage storage growth');
    recommendations.push('Consider adding NVMe storage for hot data tier');
  }

  // General capacity planning
  recommendations.push(
    `Plan capacity expansion at least ${Math.max(3, bottlenecks[0]?.monthsUntilFull - 2 || 6)} months before projected exhaustion`
  );

  return recommendations;
}

// ============================================
// COST OPTIMIZATION
// ============================================

export function analyzeCostOptimization(config: DatacenterConfig): CostOptimization {
  const suggestions: CostSuggestion[] = [];

  // Calculate current costs
  const hardwareCost = config.estimatedCost;
  const annualPowerCost = calculateAnnualPowerCost(config.estimatedPower);
  const annualCoolingCost = annualPowerCost * 0.4; // Cooling is ~40% of power
  const annualMaintenanceCost = hardwareCost * 0.1; // 10% of hardware annually

  const currentMonthlyCost =
    (annualPowerCost + annualCoolingCost + annualMaintenanceCost) / 12;

  let potentialSavings = 0;

  // Power optimization suggestions
  if (config.estimatedPower > 500) {
    const powerSavings = annualPowerCost * 0.15;
    potentialSavings += powerSavings;
    suggestions.push({
      category: 'power',
      title: 'Enable CPU Power Management',
      description:
        'Enable C-states and frequency scaling to reduce idle power consumption by up to 15%',
      estimatedSavings: Math.round(powerSavings / 12),
      effort: 'low',
      impact: 'medium',
    });
  }

  // Cooling optimization
  if (config.containerConfig) {
    const coolingSavings = annualCoolingCost * 0.2;
    potentialSavings += coolingSavings;
    suggestions.push({
      category: 'cooling',
      title: 'Optimize Airflow Layout',
      description:
        'Arrange racks for optimal hot/cold aisle separation to improve cooling efficiency by 20%',
      estimatedSavings: Math.round(coolingSavings / 12),
      effort: 'medium',
      impact: 'high',
    });
  }

  // Hardware consolidation
  const underutilizedNodes = config.nodes.filter(
    (n) => n.type === 'compute' && n.specs.cpuCores <= 4
  );
  if (underutilizedNodes.length >= 2) {
    const consolidationSavings = underutilizedNodes.length * 50 * 12; // ~$50/month/node
    potentialSavings += consolidationSavings;
    suggestions.push({
      category: 'hardware',
      title: 'Consolidate Small Nodes',
      description: `Replace ${underutilizedNodes.length} small nodes with fewer higher-capacity nodes to reduce power and maintenance`,
      estimatedSavings: Math.round(consolidationSavings / 12),
      effort: 'high',
      impact: 'high',
    });
  }

  // Storage tiering
  const totalStorage = config.nodes.reduce((acc, n) => acc + n.specs.storageGB, 0);
  if (totalStorage > 2000) {
    const storageSavings = totalStorage * 0.01 * 12; // ~$0.01/GB savings
    potentialSavings += storageSavings;
    suggestions.push({
      category: 'hardware',
      title: 'Implement Storage Tiering',
      description:
        'Move cold data to cheaper storage tier (HDD) and keep hot data on NVMe',
      estimatedSavings: Math.round(storageSavings / 12),
      effort: 'medium',
      impact: 'medium',
    });
  }

  // Scheduled shutdown
  suggestions.push({
    category: 'power',
    title: 'Scheduled Node Shutdown',
    description:
      'Power down non-essential nodes during off-hours if workload permits',
    estimatedSavings: Math.round(annualPowerCost * 0.1 / 12),
    effort: 'low',
    impact: 'low',
  });

  const optimizedMonthlyCost = currentMonthlyCost - potentialSavings / 12;

  return {
    currentMonthlyCost: Math.round(currentMonthlyCost),
    optimizedMonthlyCost: Math.round(optimizedMonthlyCost),
    annualSavings: Math.round(potentialSavings),
    suggestions: suggestions.sort((a, b) => b.estimatedSavings - a.estimatedSavings),
  };
}

function calculateAnnualPowerCost(watts: number, ratePerKwh: number = 0.12): number {
  return (watts / 1000) * 8760 * ratePerKwh;
}

// ============================================
// UPGRADE PATH RECOMMENDATIONS
// ============================================

export function recommendUpgradePaths(config: DatacenterConfig): UpgradePath[] {
  const upgrades: UpgradePath[] = [];
  const scenario = SCENARIO_PRESETS.find((s) => s.id === config.scenario);

  // Check for natural upgrade paths based on scenario
  const scenarioUpgrades: Record<ScenarioType, { next: ScenarioType; reason: string }> = {
    minimal: { next: 'lab-bench', reason: 'Expand for development and testing' },
    'lab-bench': { next: 'research', reason: 'Scale for production workloads' },
    research: { next: 'gdpr-llm', reason: 'Add AI/ML capabilities' },
    industrial: { next: 'lab-testing', reason: 'Expand measurement and DAQ capacity' },
    'gdpr-llm': { next: 'ai-rendering', reason: 'Add GPU acceleration for rendering' },
    'ai-rendering': { next: 'ai-rendering', reason: 'Already at highest tier' },
    'lab-testing': { next: 'research', reason: 'Add general compute capacity' },
    custom: { next: 'custom', reason: 'Custom configuration - evaluate specific needs' },
  };

  const nextScenario = scenarioUpgrades[config.scenario];
  if (nextScenario && nextScenario.next !== config.scenario) {
    const targetScenario = SCENARIO_PRESETS.find((s) => s.id === nextScenario.next);
    if (targetScenario) {
      upgrades.push({
        currentTier: scenario?.name || config.scenario,
        upgradeTier: targetScenario.name,
        reason: nextScenario.reason,
        estimatedCost: (targetScenario.estimatedCost || 0) - config.estimatedCost,
        benefits: [
          `Increased capacity: ${targetScenario.nodeCount} nodes`,
          `Higher performance: ~${targetScenario.estimatedPower}W power envelope`,
        ],
        timeline: '1-2 months',
        priority: 'optional',
      });
    }
  }

  // GPU upgrade if not present
  const hasGpu = config.nodes.some((n) => n.specs.gpu);
  if (!hasGpu && ['gdpr-llm', 'ai-rendering', 'research'].includes(config.scenario)) {
    upgrades.push({
      currentTier: 'No GPU',
      upgradeTier: 'GPU-Accelerated',
      reason: 'Add GPU acceleration for AI/ML workloads',
      estimatedCost: 2500,
      benefits: [
        '10-100x faster inference',
        'Enable local LLM deployment',
        'Support image/video generation',
      ],
      timeline: '1-2 weeks',
      priority: 'recommended',
    });
  }

  // Storage upgrade if running low
  const totalStorage = config.nodes.reduce((acc, n) => acc + n.specs.storageGB, 0);
  if (totalStorage < 2000 && config.nodes.length >= 3) {
    upgrades.push({
      currentTier: `${totalStorage}GB Storage`,
      upgradeTier: '4TB+ Storage',
      reason: 'Expand storage for growing data needs',
      estimatedCost: 800,
      benefits: [
        'Support larger datasets',
        'Enable data redundancy',
        'Room for logs and backups',
      ],
      timeline: '1 week',
      priority: 'recommended',
    });
  }

  // Memory upgrade for AI workloads
  const totalMemory = config.nodes.reduce((acc, n) => acc + n.specs.memoryGB, 0);
  const avgMemoryPerNode = totalMemory / config.nodes.length;
  if (avgMemoryPerNode < 32 && ['gdpr-llm', 'ai-rendering'].includes(config.scenario)) {
    upgrades.push({
      currentTier: `${Math.round(avgMemoryPerNode)}GB avg RAM`,
      upgradeTier: '64GB+ per node',
      reason: 'AI workloads require more memory',
      estimatedCost: config.nodes.length * 200,
      benefits: [
        'Run larger models',
        'Reduce swap usage',
        'Better inference performance',
      ],
      timeline: '1-2 weeks',
      priority: 'recommended',
    });
  }

  // Network upgrade
  if (config.nodes.length >= 4) {
    upgrades.push({
      currentTier: '1GbE Network',
      upgradeTier: '10GbE Network',
      reason: 'Improve inter-node communication for distributed workloads',
      estimatedCost: config.nodes.length * 150,
      benefits: [
        '10x faster node-to-node transfer',
        'Reduced storage latency',
        'Better cluster performance',
      ],
      timeline: '2-3 weeks',
      priority: 'optional',
    });
  }

  return upgrades.sort((a, b) => {
    const priorityOrder = { critical: 0, recommended: 1, optional: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
}

// ============================================
// SUMMARY FORMATTING
// ============================================

export function formatCapacityPlanSummary(
  forecast: ResourceForecast,
  costOptimization: CostOptimization,
  upgradePaths: UpgradePath[]
): string {
  const lines: string[] = [];

  lines.push('Capacity Planning Summary');
  lines.push('='.repeat(50));
  lines.push('');

  // Resource forecast
  lines.push('Resource Forecast (12 months):');
  if (forecast.bottlenecks.length === 0) {
    lines.push('  No capacity issues projected');
  } else {
    for (const b of forecast.bottlenecks) {
      lines.push(
        `  ${b.resource.toUpperCase()}: ${b.severity} - ${b.monthsUntilFull} months to capacity`
      );
    }
  }
  lines.push('');

  // Cost optimization
  lines.push('Cost Optimization:');
  lines.push(`  Current: $${costOptimization.currentMonthlyCost}/month`);
  lines.push(`  Optimized: $${costOptimization.optimizedMonthlyCost}/month`);
  lines.push(`  Annual Savings: $${costOptimization.annualSavings}`);
  lines.push('');

  // Upgrade paths
  lines.push('Recommended Upgrades:');
  for (const u of upgradePaths.slice(0, 3)) {
    lines.push(`  [${u.priority.toUpperCase()}] ${u.upgradeTier} (+$${u.estimatedCost})`);
  }

  return lines.join('\n');
}
